/* Työmääräykset ja työjono widgets. Thin data mapping over the shared templates. */
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import BuildIcon from '@mui/icons-material/Build';
import CancelIcon from '@mui/icons-material/Cancel';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DateRangeIcon from '@mui/icons-material/DateRange';
import EventIcon from '@mui/icons-material/Event';
import EventBusyIcon from '@mui/icons-material/EventBusy';
import HourglassTopIcon from '@mui/icons-material/HourglassTop';
import KeyIcon from '@mui/icons-material/Key';
import TaskAltIcon from '@mui/icons-material/TaskAlt';
import TimerOffIcon from '@mui/icons-material/TimerOff';
import ViewKanbanIcon from '@mui/icons-material/ViewKanban';

import { statusConfig } from '../../shared/StatusBadge';
import { sortMaintenancesByPriority } from '../../../utils/maintenanceUtils';
import { formatDate } from '../../../utils';
import { dataSources } from '../dataSources';
import { useDashboardActions } from '../dashboardActions';
import { useWidgetFrame } from '../frameContext';
import BreakdownTile from '../templates/BreakdownTile';
import KpiTile from '../templates/KpiTile';
import ListBody, { SummaryText } from '../templates/ListBody';
import { addDaysIso, fmtDateTime, fmtDay, fmtEur0, fmtEur2, fmtH, fmtNumber, hh, todayIso } from '../templates/format';
import { listOf, useHeader, useOrg, useSource } from '../widgetHooks';
import { can } from '../../../utils/access';
import { customerLabel } from '../../../utils/customer';

const vehicleLabel = (vehicle) => [vehicle?.manufacturer, vehicle?.model, vehicle?.year_model].filter(Boolean).join(' ');

// ---------------------------------------------------------------- KPI tiles

export function ActiveTile() {
  const { t } = useTranslation();
  const { navigateToMaintenancesWithFilters } = useDashboardActions();
  const { data } = useSource(dataSources.stats());
  const todayNew = data?.today_new_maintenances ?? 0;
  return (
    <KpiTile
      icon={BuildIcon}
      value={data?.active_maintenances ?? 0}
      label={t('dashboard.widgets.active.title')}
      delta={todayNew > 0 ? `+${todayNew} ${t('Today').toLowerCase()}` : null}
      onClick={() => navigateToMaintenancesWithFilters({ states: ['planned', 'in_progress'] })}
      testId="stat-active-maintenances"
    />
  );
}

export function PickupTile() {
  const { t } = useTranslation();
  const { navigateToMaintenancesWithFilters } = useDashboardActions();
  const { data } = useSource(dataSources.stats());
  return (
    <KpiTile
      icon={KeyIcon}
      value={data?.awaiting_pickup ?? 0}
      label={t('dashboard.widgets.pickup.title')}
      onClick={() => navigateToMaintenancesWithFilters({ states: ['completed'] })}
      testId="stat-awaiting-pickup"
    />
  );
}

export function ThisWeekTile() {
  const { t } = useTranslation();
  const { navigateToMaintenancesWithFilters } = useDashboardActions();
  const { data } = useSource(dataSources.stats());
  const trend = data?.this_week_trend ?? 0;
  return (
    <KpiTile
      icon={DateRangeIcon}
      value={data?.this_week_maintenances ?? 0}
      label={t('dashboard.widgets.thisweek.title')}
      trend={trend !== 0 ? { text: `${trend > 0 ? '+' : ''}${trend} %`, up: trend >= 0 } : null}
      onClick={() => navigateToMaintenancesWithFilters({ dateRangeType: 'this_week' })}
      testId="stat-this-week"
    />
  );
}

export function QueueStatesWidget() {
  const { t } = useTranslation();
  const { navigateToMaintenancesWithFilters } = useDashboardActions();
  const { data } = useSource(dataSources.stats());
  const planned = data?.planned_count ?? 0;
  const inProgress = data?.in_progress_count ?? 0;
  const waiting = data?.waiting_count ?? 0;
  const inWorkshop = data?.vehicle_in_workshop_count ?? 0;
  const done = data?.awaiting_pickup ?? 0;
  const chip = (state, n, labelKey) => ({
    n,
    label: t(labelKey),
    color: statusConfig[state].color,
    bg: statusConfig[state].bg,
    onClick: () => navigateToMaintenancesWithFilters({ states: [state] }),
  });
  useHeader({ empty: data ? planned + inProgress + waiting + inWorkshop + done === 0 : false }, [data, planned, inProgress, waiting, inWorkshop, done]);
  return (
    <BreakdownTile
      icon={ViewKanbanIcon}
      value={planned + inProgress + waiting + inWorkshop}
      unit={t('dashboard.chips.inQueue')}
      chips={[
        chip('planned', planned, 'dashboard.chips.planned'),
        chip('in_progress', inProgress, 'dashboard.chips.inProgress'),
        chip('waiting', waiting, 'dashboard.chips.waiting'),
        ...(inWorkshop ? [chip('vehicle_in_workshop', inWorkshop, 'dashboard.chips.inWorkshop')] : []),
        chip('completed', done, 'dashboard.chips.doneLower'),
      ]}
    />
  );
}

export function MyInProgressTile({ opts }) {
  const { t } = useTranslation();
  const { data } = useSource(dataSources.stats());
  return <KpiTile icon={BuildIcon} iconColor="#f59e0b" iconBg="rgba(245,158,11,0.12)" value={data?.in_progress_count ?? 0} label={t('dashboard.widgets.my_inprogress.title')} centered={Boolean(opts?.centered)} />;
}

export function MyScheduledTile({ opts }) {
  const { t } = useTranslation();
  const { data } = useSource(dataSources.stats());
  return <KpiTile icon={EventIcon} iconColor="#3182ce" iconBg="rgba(49,130,206,0.12)" value={data?.planned_count ?? 0} label={t('dashboard.widgets.my_scheduled.title')} centered={Boolean(opts?.centered)} />;
}

export function MyDoneTile({ opts }) {
  const { t } = useTranslation();
  const { data } = useSource(dataSources.stats());
  return <KpiTile icon={TaskAltIcon} iconColor="#10b981" iconBg="rgba(16,185,129,0.12)" value={data?.awaiting_pickup ?? 0} label={t('dashboard.widgets.my_done.title')} centered={Boolean(opts?.centered)} />;
}

// ---------------------------------------------------------------- lists

const ACTIVE_PARAMS = { state: 'in_progress,planned', page_size: 25 };

export function ActiveListWidget() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { data } = useSource(dataSources.maintenances(ACTIVE_PARAMS, 'active_list'));
  const rows = useMemo(() => {
    const relevant = listOf(data).filter((m) => m.state === 'in_progress' || (m.state === 'planned' && m.schedules?.length > 0));
    return sortMaintenancesByPriority(relevant).map((m) => ({
      key: m.id,
      state: m.state,
      plate: m.vehicle?.registration_number,
      primary: customerLabel(m.customer) || t('No customer'),
      secondary: [vehicleLabel(m.vehicle), m.description].filter(Boolean).join(' · '),
      pill: m.state,
      right: formatDate(m.created_at)?.split(' ')[0],
      rightMono: false,
      rightTone: 'muted',
      onClick: () => navigate(`/maintenances/${m.id}`),
      testId: `widget-active_list-row-${m.id}`,
    }));
  }, [data, navigate, t]);
  useHeader({ link: { labelKey: 'dashboard.actions.showAll', to: '/maintenances' }, empty: Boolean(data) && rows.length === 0 }, [data, rows.length]);
  return <ListBody rows={rows} onMore={() => navigate('/maintenances')} />;
}

const MY_LIST_PARAMS = { state: 'planned,in_progress,completed,waiting,vehicle_in_workshop', page_size: 25 };

function scheduleLabel(schedule, t) {
  if (!schedule?.start_time) return null;
  const date = new Date(schedule.start_time);
  const today = new Date();
  const tomorrow = new Date(today);
  tomorrow.setDate(tomorrow.getDate() + 1);
  const time = `${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`;
  if (date.toDateString() === today.toDateString()) return `${t('Today')} ${time}`;
  if (date.toDateString() === tomorrow.toDateString()) return `${t('Tomorrow')} ${time}`;
  return `${date.toLocaleDateString('fi-FI', { weekday: 'short', day: 'numeric', month: 'numeric' })} ${time}`;
}

export function MyListWidget() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { data } = useSource(dataSources.maintenances(MY_LIST_PARAMS, 'my_list'));
  const rows = useMemo(
    () =>
      sortMaintenancesByPriority(listOf(data), { completedLast: true }).map((m) => ({
        key: m.id,
        plate: m.vehicle?.registration_number || t('No registration number'),
        pill: m.state,
        primary: vehicleLabel(m.vehicle) || m.description || '',
        secondary: m.description || m.customer?.name || '',
        chip: scheduleLabel(m.schedules?.[0], t),
        highlight: m.state === 'in_progress',
        onClick: () => navigate(`/maintenances/${m.id}`),
        testId: `widget-my_list-row-${m.id}`,
      })),
    [data, navigate, t]
  );
  useHeader({ link: { labelKey: 'dashboard.actions.showAll', to: '/maintenances' }, empty: Boolean(data) && rows.length === 0 }, [data, rows.length]);
  return <ListBody rows={rows} onMore={() => navigate('/maintenances')} />;
}

function useDayQueue(dateIso, opts) {
  const { data } = useSource(dataSources.calendarDay(dateIso));
  return useMemo(() => {
    const events = (data?.events || []).filter((event) => event.local_start_date === dateIso);
    const filtered = opts?.employee ? events.filter((event) => (event.employee_ids || []).includes(Number(opts.employee))) : events;
    return filtered.slice().sort((a, b) => (a.local_start_hour ?? 0) - (b.local_start_hour ?? 0));
  }, [data, dateIso, opts?.employee]);
}

const QUEUE_CHIPS = [
  { key: 'all', labelKey: 'dashboard.chips.all' },
  { key: 'started', labelKey: 'dashboard.chips.started', state: 'in_progress' },
  { key: 'done', labelKey: 'dashboard.chips.done', state: 'completed' },
  { key: 'not_started', labelKey: 'dashboard.chips.notStarted', state: 'planned' },
];
const bucketOf = (state) => (state === 'in_progress' ? 'started' : state === 'completed' || state === 'picked_up' ? 'done' : 'not_started');

function DayQueue({ dateIso, opts, chips, chipLabel }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const events = useDayQueue(dateIso, opts);
  const [active, setActive] = useState('all');
  const counts = useMemo(() => {
    const c = { all: events.length, started: 0, done: 0, not_started: 0 };
    events.forEach((event) => {
      c[bucketOf(event.state)] += 1;
    });
    return c;
  }, [events]);
  const rows = useMemo(
    () =>
      events
        .filter((event) => active === 'all' || bucketOf(event.state) === active)
        .map((event) => ({
          key: event.id,
          state: bucketOf(event.state) === 'not_started' ? 'cancelled' : event.state,
          time: hh(event.local_start_hour),
          plate: event.vehicle_registration,
          primary: event.description || [event.vehicle_manufacturer, event.vehicle_model].filter(Boolean).join(' '),
          secondary: event.customer_name,
          right: fmtH((event.local_end_hour ?? 0) - (event.local_start_hour ?? 0)),
          onClick: () => navigate(`/maintenances/${event.maintenance_id}`),
        })),
    [events, active, navigate]
  );
  useHeader({ chip: chipLabel, link: { labelKey: 'dashboard.actions.openCalendar', to: '/calendar' }, empty: events.length === 0 }, [chipLabel, events.length]);
  return (
    <ListBody
      rows={rows}
      chips={chips ? QUEUE_CHIPS.map((chip) => ({ key: chip.key, label: t(chip.labelKey), n: counts[chip.key], state: chip.state })) : undefined}
      activeChip={active}
      onChip={setActive}
      onMore={() => navigate('/calendar')}
    />
  );
}

export function TodayQueueWidget({ opts }) {
  const { t } = useTranslation();
  const offset = Number(opts?.dayOffset) || 0;
  const dateIso = addDaysIso(offset);
  const label = offset === 0 ? t('dashboard.chips.today') : offset === 1 ? t('dashboard.chips.tomorrow') : fmtDateTime(dateIso).split(' ')[0];
  return <DayQueue dateIso={dateIso} opts={opts} chips chipLabel={label} />;
}

export function TomorrowWidget({ opts }) {
  const { t } = useTranslation();
  const offset = opts?.dayOffset != null ? Number(opts.dayOffset) : 1;
  return <DayQueue dateIso={addDaysIso(offset)} opts={{ ...opts, dayOffset: offset }} chipLabel={offset === 1 ? t('dashboard.chips.tomorrow') : t('dashboard.chips.today')} />;
}

export function UnscheduledWidget() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const frame = useWidgetFrame();
  const { data } = useSource(dataSources.unscheduled(10));
  const results = data?.results || [];
  const total = data?.total_count ?? results.length;
  const thisWeek = results.filter((row) => row.created_at && Date.now() - new Date(row.created_at).getTime() < 7 * 86400000).length;
  useHeader({ count: total, link: { labelKey: 'dashboard.actions.openCalendar', to: '/calendar' }, empty: Boolean(data) && total === 0 }, [data, total]);
  if (frame?.headless) {
    return (
      <BreakdownTile
        icon={EventBusyIcon}
        value={total}
        unit={t('dashboard.chips.workOrders')}
        chips={[{ n: thisWeek, label: t('dashboard.chips.withinWeek'), color: statusConfig.waiting.color, bg: statusConfig.waiting.bg }, { n: Math.max(0, total - thisWeek), label: t('dashboard.chips.older') }]}
        onClick={() => navigate('/calendar')}
      />
    );
  }
  const rows = results.map((row) => ({
    key: row.id,
    icon: EventBusyIcon,
    plate: row.vehicle_registration,
    primary: row.customer_name || t('No customer'),
    secondary: [row.work_number ? `#${row.work_number}` : null, row.description].filter(Boolean).join(' · '),
    onClick: () => navigate(`/maintenances/${row.id}`),
  }));
  return <ListBody rows={rows} totalCount={total} onMore={() => navigate('/calendar')} />;
}

export function QuotesWidget({ opts }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const org = useOrg();
  const { open, navigateToMaintenancesWithFilters } = useDashboardActions();
  const { data } = useSource(dataSources.quotes(25));
  const limit = Number(opts?.ageLimit) || 14;
  const items = data?.items || [];
  const link = org?.separate_quotes_list_enabled ? { labelKey: 'dashboard.actions.showAll', to: '/quotes' } : { labelKey: 'dashboard.actions.showAll', onClick: () => navigateToMaintenancesWithFilters({ states: ['quote'] }) };
  useHeader({ link, empty: Boolean(data) && items.length === 0 }, [data, items.length, org?.separate_quotes_list_enabled]);
  // quote_status comes from the newest sent snapshot (or the legacy "offer sent" event):
  // not_sent | sent | awaiting | accepted | declined | expired. Workshops that never ask
  // for acceptance only ever see "sent" / "not sent".
  const statusText = (quote) => {
    const status = quote.quote_status || 'not_sent';
    return t(`dashboard.quoteStatus.${status}`, { date: quote.quote_sent_at ? fmtDay(quote.quote_sent_at) : '' }).trim();
  };
  const rows = items.map((quote) => ({
    key: quote.id,
    state: 'quote',
    plate: quote.registration_number,
    primary: quote.customer_name || t('No customer'),
    secondary: [quote.work_number ? `#${quote.work_number}` : null, statusText(quote)].filter(Boolean).join(' · '),
    right: fmtEur2(quote.total_excl_vat),
    chip: `${quote.age_days} ${t('dashboard.chips.daysShort')}`,
    chipTone: quote.quote_status === 'accepted' ? 'green' : quote.age_days > limit ? 'red' : 'neutral',
    onClick: () => navigate(`/maintenances/${quote.id}`),
  }));
  const summaryParts = data
    ? [
        { strong: true, text: t('dashboard.chips.quotesCount', { count: data.count }) },
        { text: t('dashboard.chips.total') },
        { strong: true, mono: true, text: fmtEur2(data.total_excl_vat) },
        ...(data.awaiting_count > 0
          ? [{ text: '·' }, { strong: true, text: t('dashboard.chips.quotesAwaiting', { count: data.awaiting_count }) }]
          : []),
      ]
    : null;
  const summary = summaryParts ? <SummaryText parts={summaryParts} /> : null;
  void open;
  return <ListBody rows={rows} totalCount={data?.count} summary={summary} onMore={link.to ? () => navigate(link.to) : link.onClick} />;
}

const APPROVAL_STYLE = {
  extra_work_pending: { icon: HourglassTopIcon, color: '#ed8936' },
  confirmation_pending: { icon: HourglassTopIcon, color: '#ed8936' },
  confirmation_expired: { icon: TimerOffIcon, color: '#c53030' },
  extra_work_approved: { icon: CheckCircleIcon, color: '#2f8c54' },
  confirmation_confirmed: { icon: CheckCircleIcon, color: '#2f8c54' },
  extra_work_declined: { icon: CancelIcon, color: '#c53030' },
  confirmation_declined: { icon: CancelIcon, color: '#c53030' },
  quote_pending: { icon: HourglassTopIcon, color: '#ed8936' },
  quote_expired: { icon: TimerOffIcon, color: '#c53030' },
  quote_accepted: { icon: CheckCircleIcon, color: '#2f8c54' },
  quote_declined: { icon: CancelIcon, color: '#c53030' },
};

export function ApprovalsWidget({ opts }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  // Pending items are always listed; the setting only bounds how far back answered ones go.
  const days = Number(opts?.responseDays) || 7;
  const { data } = useSource(dataSources.approvals(25, days));
  const items = data?.items || [];
  useHeader(
    {
      chip: data?.pending_count ? String(data.pending_count) : null,
      chipTone: 'amber',
      link: { labelKey: 'dashboard.actions.showAll', to: '/maintenances' },
      empty: Boolean(data) && items.length === 0,
      // Name the window in the empty state too: an empty widget is where a workshop whose
      // last acceptance is older than the window goes looking for a bug. A key, not a
      // translated string, so the frame re-translates it when the language changes.
      emptyTextKey: 'dashboard.empty.approvalsWindow',
      emptyTextParams: { count: data?.window_days || days },
    },
    [data, items.length, days]
  );
  const rows = items.map((item, index) => {
    const style = APPROVAL_STYLE[item.kind] || APPROVAL_STYLE.extra_work_pending;
    return {
      key: `${item.kind}-${item.maintenance_id}-${item.extra_work_request_id || index}`,
      icon: style.icon,
      iconColor: style.color,
      plate: item.registration_number,
      primary: `${t(`dashboard.approvals.${item.kind}`)}${item.title ? `: ${item.title}` : ''}`,
      secondary: [item.customer_name, item.note].filter(Boolean).join(' · '),
      right: fmtDateTime(item.at),
      rightMono: false,
      rightTone: 'muted',
      action: { label: t('dashboard.actions.open'), onClick: () => navigate(`/maintenances/${item.maintenance_id}`) },
      onClick: () => navigate(`/maintenances/${item.maintenance_id}`),
    };
  });
  // Say how far back the answers go: to a workshop whose last acceptance is older than the
  // window, an empty widget otherwise looks like a bug.
  const summary = data ? <SummaryText parts={[{ text: t('dashboard.chips.answersWindow', { count: data.window_days || days }) }]} /> : null;
  return <ListBody rows={rows} summary={summary} onMore={() => navigate('/maintenances')} />;
}

export function UninvoicedWidget() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const frame = useWidgetFrame();
  const { navigateToMaintenancesWithFilters } = useDashboardActions();
  const org = useOrg();
  const { data } = useSource(dataSources.invoiceable());
  const items = Array.isArray(data) ? data : [];
  const total = items.reduce((sum, row) => sum + (Number(row.total_amount) || 0), 0);
  // The summed euro is a revenue figure; without reports.view the headline is the count instead.
  const showRevenue = can(org, 'reports.view');
  const overWeek = items.filter((row) => row.completed_at && Date.now() - new Date(row.completed_at).getTime() > 7 * 86400000).length;
  const openList = () => navigateToMaintenancesWithFilters({ states: ['completed', 'picked_up'], invoiceStatus: 'no_invoice' });
  useHeader({ link: { labelKey: 'dashboard.actions.showAll', onClick: openList }, empty: Boolean(data) && items.length === 0 }, [data, items.length]);
  if (frame?.headless) {
    return (
      <BreakdownTile
        icon={TaskAltIcon}
        value={showRevenue ? fmtEur0(total) : fmtNumber(items.length)}
        unit={showRevenue ? t('dashboard.chips.exclVat') : t('dashboard.chips.workOrders')}
        chips={[
          ...(showRevenue
            ? [{ n: items.length, label: t('dashboard.chips.workOrders'), color: statusConfig.completed.color, bg: statusConfig.completed.bg }]
            : []),
          { n: overWeek, label: t('dashboard.chips.overWeek'), color: statusConfig.cancelled.color, bg: statusConfig.cancelled.bg },
        ]}
        onClick={openList}
      />
    );
  }
  const rows = items.map((row) => ({
    key: row.id,
    state: 'completed',
    plate: row.vehicle?.registration_number,
    primary: customerLabel(row.customer) || t('No customer'),
    secondary: [row.work_number ? `#${row.work_number}` : null, row.completed_at ? `${t('dashboard.chips.completedAt')} ${fmtDateTime(row.completed_at)}` : null].filter(Boolean).join(' · '),
    right: fmtEur2(row.total_amount),
    action: {
      label: t('dashboard.actions.createInvoice'),
      variant: 'primary',
      testId: `uninvoiced-create-invoice-${row.id}`,
      onClick: () => navigate(`/maintenances/${row.id}?createInvoice=1`),
    },
    onClick: () => navigate(`/maintenances/${row.id}`),
  }));
  const summary = data ? (
    <SummaryText parts={[{ strong: true, text: t('dashboard.chips.workOrdersCount', { count: items.length }) }, { strong: true, mono: true, text: fmtEur0(total) }, { text: t('dashboard.chips.uninvoiced') }]} />
  ) : null;
  return <ListBody rows={rows} summary={summary} onMore={openList} />;
}

const WAITING_PARAMS = { state: 'waiting', page_size: 25 };

export function WaitingWidget() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const org = useOrg();
  const { navigateToMaintenancesWithFilters } = useDashboardActions();
  const { data } = useSource(dataSources.maintenances(WAITING_PARAMS, 'waiting'));
  const items = listOf(data);
  useHeader({ link: { labelKey: 'dashboard.actions.showAll', onClick: () => navigateToMaintenancesWithFilters({ states: ['waiting'] }) }, empty: Boolean(data) && items.length === 0 }, [data, items.length]);
  const rows = items.map((m) => ({
    key: m.id,
    state: 'waiting',
    plate: m.vehicle?.registration_number,
    primary: customerLabel(m.customer) || t('No customer'),
    secondary: m.work_number ? `#${m.work_number}` : m.description,
    chip: m.waiting_reason ? t(`dashboard.waitingReason.${m.waiting_reason}`, { defaultValue: m.waiting_reason }) : null,
    chipTone: 'amber',
    right: org?.parts_order_tracking_enabled && m.parts_order_status && m.parts_order_status !== 'not_ordered' ? t(`dashboard.partsStatus.${m.parts_order_status}`, { defaultValue: m.parts_order_status }) : null,
    rightMono: false,
    rightTone: m.parts_order_status === 'arrived' ? undefined : 'muted',
    onClick: () => navigate(`/maintenances/${m.id}`),
  }));
  return <ListBody rows={rows} onMore={() => navigateToMaintenancesWithFilters({ states: ['waiting'] })} />;
}

export const _internal = { todayIso };
